// Email verification for the portal.
//
// The API has issued signup verification codes all along — Register and Login
// both return code "email_not_verified" and mail a six-digit OTP — but the
// portal had no screen that could submit one. The only OTP page it owned calls
// app/auth/otp-verify, which is the forgot-password flow and ends at a password
// reset. So a new admin received a code and had nowhere to type it, which is
// exactly where this page comes in.
//
// Verifying returns a full session (token + refreshToken), so a successful
// verification logs the admin in rather than bouncing them back to the login
// form to type their password a second time.
import { useEffect, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { api, common } from "includes";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();

  // The email arrives from whichever screen sent us here. tempData is the
  // fallback so a page refresh mid-verification doesn't strand the admin.
  const passed = (location.state as any)?.email;
  const [email] = useState<string>(passed || common.tempDataGet() || "");

  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!email) {
      // Nothing to verify against — send them back rather than showing a form
      // that can only fail.
      navigate("/login");
      return;
    }
    common.tempDataSet(email);
  }, [email, navigate]);

  const verify = (e: any) => {
    e.preventDefault();
    const code = otp.trim();
    if (!code) {
      setError("Enter the code from your email.");
      return;
    }

    setBusy(true);
    setError("");
    api.call(
      {
        url: "app/auth/verify-email-otp",
        method: "POST",
        auth: "none",
        body: { email, otp: code },
      },
      (res: any) => {
        setBusy(false);
        const data = res?.data?.data;

        if (res.status === 200 && res.data?.code === "success") {
          // Same admin-tier gate the login screen applies — verifying an email
          // must not become a side door into the portal for a basic user.
          if (!["super", "admin"].includes(data?.role)) {
            common.notify("E", "Only allowed admin users");
            navigate("/login");
            return;
          }
          common.localSet("authData", common.crypt(data, true));
          common.tempDataRemove();
          common.notify("S", `Welcome ${data?.firstName}!`);
          setTimeout(() => {
            navigate(
              data?.permission?.includes("inc")
                ? "/incident/form?mode=add"
                : "/app/home"
            );
          }, 500);
          return;
        }

        if (res.data?.code === "already_registered_login") {
          common.notify("S", "That email is already verified — please log in.");
          navigate("/login");
          return;
        }

        setError(res?.data?.message || "That code was not accepted.");
      }
    );
  };

  const resend = () => {
    setResending(true);
    setError("");
    api.call(
      {
        url: "app/auth/resend-email-otp",
        method: "POST",
        auth: "none",
        body: { email },
      },
      (res: any) => {
        setResending(false);
        if (res.status === 200) {
          common.notify("S", "A new code is on its way.");
        } else {
          setError(res?.data?.message || "Could not send a new code.");
        }
      }
    );
  };

  return (
    <Row className="justify-content-center">
      <Col md={6} lg={5}>
        <Card>
          <Card.Body className="p-4">
            <h4 className="mb-1">Verify your email</h4>
            <p className="text-muted mb-3">
              We sent a six-digit code to <strong>{email}</strong>. Enter it
              below to finish setting up your account. The code expires after
              10 minutes.
            </p>

            <Form onSubmit={verify}>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="fw-otp">Verification code</Form.Label>
                <Form.Control
                  id="fw-otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="000000"
                  value={otp}
                  autoFocus
                  onChange={(e) => setOtp(e.target.value)}
                />
                {error && <p className="text-danger mt-1 mb-0">{error}</p>}
              </Form.Group>

              <div className="d-grid mb-2">
                <Button type="submit" variant="primary" disabled={busy}>
                  {busy ? "Verifying…" : "Verify and continue"}
                </Button>
              </div>
            </Form>

            <div className="d-flex justify-content-between">
              <Button
                variant="link"
                className="px-0"
                disabled={resending}
                onClick={resend}
              >
                {resending ? "Sending…" : "Send a new code"}
              </Button>
              <Link to="/login" className="align-self-center">
                Back to sign in
              </Link>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
}
